/**
 * GlowRise — Theme JS
 * Global animation init: GSAP + ScrollTrigger + Lenis + Splitting
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
   * 1. GSAP + ScrollTrigger registration
   * ───────────────────────────────────────── */
  gsap.registerPlugin(ScrollTrigger);

  /* ─────────────────────────────────────────
   * 2. Lenis smooth scroll
   * ───────────────────────────────────────── */
  const lenis = new Lenis({
    duration: 1.15,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    gestureOrientation: 'vertical',
    smoothWheel: true,
  });

  // Sync Lenis RAF with GSAP ticker
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // Sync ScrollTrigger scroll position with Lenis
  lenis.on('scroll', () => ScrollTrigger.update());

  // Expose globally so sections can access it
  window.glowrise = window.glowrise || {};
  window.glowrise.lenis = lenis;

  /* ─────────────────────────────────────────
   * 3. Splitting.js — text split init
   *    Splits any [data-split] element into
   *    chars / words / lines automatically
   * ───────────────────────────────────────── */
  Splitting();

  /* ─────────────────────────────────────────
   * 4. Global scroll animations
   * ───────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initFadeAnimations();
    initTextReveals();
    initParallax();
    initImageReveal();
    initCounters();

    // Signal that GSAP + Lenis + Splitting are ready
    window.glowrise.ready = true;
    window.dispatchEvent(new CustomEvent('glowrise:ready'));
  });

  /* ── Fade animations ──────────────────────
   * Usage: <div data-animate="fade-up">
   *        <div data-animate="fade-up" data-delay="0.2">
   *
   * Types: fade-up | fade-down | fade-left | fade-right | fade | scale-up
   */
  function initFadeAnimations() {
    const presets = {
      'fade-up':    { y: 50,  opacity: 0 },
      'fade-down':  { y: -50, opacity: 0 },
      'fade-left':  { x: 50,  opacity: 0 },
      'fade-right': { x: -50, opacity: 0 },
      'fade':       { opacity: 0 },
      'scale-up':   { scale: 0.92, opacity: 0 },
    };

    gsap.utils.toArray('[data-animate]').forEach((el) => {
      const type  = el.dataset.animate;
      const delay = parseFloat(el.dataset.delay || 0);
      const from  = presets[type] ?? { opacity: 0 };

      gsap.from(el, {
        ...from,
        duration: 1,
        delay,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          once: true,
        },
      });
    });
  }

  /* ── Text reveals ─────────────────────────
   * Usage: <h2 data-split="words">Title</h2>
   *        <p  data-split="lines">Text</p>
   *        <h1 data-split="chars">Big</h1>
   *
   * Split types: chars | words | lines
   */
  function initTextReveals() {
    gsap.utils.toArray('[data-split]').forEach((el) => {
      const by      = el.dataset.split || 'words';
      const delay   = parseFloat(el.dataset.delay || 0);
      const stagger = by === 'chars' ? 0.025 : by === 'words' ? 0.06 : 0.1;

      // Splitting already ran globally — retrieve result
      const result = Splitting({ target: el, by });
      const items  = result[0]?.[by];
      if (!items?.length) return;

      // Wrap lines in overflow:hidden to clip the rise
      if (by === 'lines') {
        items.forEach((line) => {
          const wrapper = document.createElement('span');
          wrapper.style.display = 'block';
          wrapper.style.overflow = 'hidden';
          line.parentNode.insertBefore(wrapper, line);
          wrapper.appendChild(line);
        });
      }

      gsap.from(items, {
        y: by === 'chars' || by === 'words' ? '110%' : '105%',
        opacity: by === 'chars' ? 0 : 1,
        duration: 0.85,
        delay,
        stagger,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: el,
          start: 'top 88%',
          once: true,
        },
      });
    });
  }

  /* ── Parallax ─────────────────────────────
   * Usage: <div data-parallax="0.3">
   *
   * Speed: positive = moves up as you scroll down (subtle)
   *        negative = moves down
   */
  function initParallax() {
    gsap.utils.toArray('[data-parallax]').forEach((el) => {
      const speed = parseFloat(el.dataset.parallax || 0.25);

      gsap.to(el, {
        y: () => -(el.offsetHeight * speed),
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('[data-parallax-container]') || el.parentElement,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    });
  }

  /* ── Image reveal (clip-path) ─────────────
   * Usage: <div data-reveal="left">
   *
   * Directions: left | right | top | bottom | center
   */
  function initImageReveal() {
    const clips = {
      left:   ['inset(0 100% 0 0)', 'inset(0 0% 0 0)'],
      right:  ['inset(0 0 0 100%)', 'inset(0 0 0 0%)'],
      top:    ['inset(100% 0 0 0)', 'inset(0% 0 0 0)'],
      bottom: ['inset(0 0 100% 0)', 'inset(0 0 0% 0)'],
      center: ['inset(50% 0)', 'inset(0% 0)'],
    };

    gsap.utils.toArray('[data-reveal]').forEach((el) => {
      const dir      = el.dataset.reveal || 'left';
      const delay    = parseFloat(el.dataset.delay || 0);
      const [from, to] = clips[dir] ?? clips.left;

      gsap.fromTo(el,
        { clipPath: from },
        {
          clipPath: to,
          duration: 1.2,
          delay,
          ease: 'power4.inOut',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            once: true,
          },
        }
      );
    });
  }

  /* ── Number counters ──────────────────────
   * Usage: <span data-counter="1500">0</span>
   *        <span data-counter="99" data-suffix="%">0</span>
   */
  function initCounters() {
    gsap.utils.toArray('[data-counter]').forEach((el) => {
      const target = parseFloat(el.dataset.counter);
      const suffix = el.dataset.suffix || '';
      const obj    = { val: 0 };

      gsap.to(obj, {
        val: target,
        duration: 1.8,
        ease: 'power2.out',
        onUpdate: () => {
          el.textContent = Math.round(obj.val).toLocaleString() + suffix;
        },
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
          once: true,
        },
      });
    });
  }

})();
